/**
 * LIFF 初始化薄包。
 *
 * useLiff hook 處理三狀態：
 *   - status: 'loading'       → LIFF SDK 還在 init
 *   - status: 'not-logged-in' → LIFF init OK 但 user 未登入（需 liff.login()）
 *   - status: 'ready'         → 可以拿 idToken 打 api
 *   - status: 'error'         → SDK init 失敗（非 LINE 環境？LIFF ID 錯？）
 */
import liff from '@line/liff';
import { useEffect, useState } from 'react';

export type LiffStatus =
  | { kind: 'loading' }
  | { kind: 'not-logged-in'; login: () => void }
  | { kind: 'ready'; idToken: string | null; profile: { lineUserId: string | null } }
  | { kind: 'error'; message: string };

export function useLiff(liffId: string | undefined): LiffStatus {
  const [status, setStatus] = useState<LiffStatus>({ kind: 'loading' });

  useEffect(() => {
    if (!liffId) {
      setStatus({
        kind: 'error',
        message: 'LIFF ID 未設定（檢查 VITE_LIFF_ID_CHART / VITE_LIFF_ID_PROFILE）',
      });
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        await liff.init({ liffId });
      } catch (err) {
        if (cancelled) return;
        setStatus({
          kind: 'error',
          message: err instanceof Error ? err.message : String(err),
        });
        return;
      }

      if (cancelled) return;

      if (!liff.isLoggedIn()) {
        setStatus({
          kind: 'not-logged-in',
          login: () => liff.login(),
        });
        return;
      }

      const idToken = liff.getIDToken();
      // context 在 LINE 裡才會有；瀏覽器 preview 會是 null
      const ctx = liff.getContext();
      setStatus({
        kind: 'ready',
        idToken: idToken ?? null,
        profile: { lineUserId: ctx?.userId ?? null },
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [liffId]);

  return status;
}
